<template>
  <a-dialog v-model="show" width="800px" max-width="80vw" persistent scrollable>
    <v-card>
      <v-banner color="primaryLight1" sticky single-line tile>
        <v-toolbar color="primaryLight1">
          <span>{{
            commentData && commentData.id
              ? $t("chat.lblUpdateMessage")
              : $t("chat.lblAddMessage")
          }}</span>
          <v-spacer></v-spacer>
          <CloseIcon @click="close()" />
        </v-toolbar>
      </v-banner>
      <v-card-text style="color: black">
        <v-row>
          <v-col cols="12">
            <a-rich-text-editor
              v-if="showTextEditor"
              v-model="formData.comment"
              @descriptionChange="descriptionChanges"
              required
            ></a-rich-text-editor>
            <div class="error-element" v-if="commentValidation">
              {{ commentValidation }}
            </div>
          </v-col>
          <v-col cols="12">
            <upload-file
              :files="formData.documents"
              :icons="fileIcons"
              :rules="rules"
              :clearList="show"
              v-on:add-files="addUploadingFiles"
              v-on:remove-file="removeUploadingFile"
              :accept="allowed_file"
              v-on:file-error="fileError"
            ></upload-file>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              class="mr-2"
              color="primaryDark"
              small
              v-bind="attrs"
              v-on="on"
              @click="saveComment"
              :disabled="
                messageValidation !== null || errorCount > 0 || !isDirtyData || isDescriptionWithImage
              "
              ><span>{{ $t("btnOk") }}</span>
            </a-btn>
          </template>
          <span> {{ $t("btnOk") }} </span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              color="secondary"
              small
              @click="close()"
              v-bind="attrs"
              v-on="on"
              ><span>{{ $t("btnCancel") }}</span>
            </a-btn>
          </template>
          <span> {{ $t("btnCancel") }} </span>
        </v-tooltip>
      </v-card-actions>
      <MessageSheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></MessageSheet>
      <confirm-dialog ref="confirm"></confirm-dialog>
    </v-card>
  </a-dialog>
</template>

<script>
import {
  MAX_UPLOAD_SIZE,
  allowed_file,
  fileIcons,
} from "../../../utils/constant";
import MessageSheet from "@/components/MessageSheet.vue";
import { formatBytes, currentDateTime, createUUID } from "../../../utils/util";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import UploadFile from "@/components/documents/UploadFile.vue";
import { mapState, mapActions } from "vuex";
import _ from "lodash";
import InsightsMixin from "../../../mixin/InsightsMixin";

export default {
  name: "ChatForm",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    meetingObject: {
      type: Object,
      default: true,
    },
    commentData: {
      type: Object,
      default: null,
    },
    chatId: {
      type: String,
      required: true,
    },
  },
  components: {
    MessageSheet,
    ConfirmDialog,
    UploadFile,
  },
  mixins: [InsightsMixin],
  data() {
    return {
      isDescriptionWithImage: false,
      isPrevent: false,
      originalFormData: {},
      uploadingFiles: [],
      showTextEditor: false,
      formData: {
        chat_id: this.chatId,
        meetingTypeId: this.meetingObject.meetingTypeId,
        meetingid: this.meetingObject.id,
        comment: this.commentData ? this.commentData.comment : "",
        documents: this.commentData
          ? _.cloneDeep(this.commentData.documents)
          : [],
        updated_at: new Date().toISOString(),
        master_message_id: this.commentData
          ? this.commentData.master_message_id
          : "",
      },
      deleteDocumentIds: [],
      displayMessage: false,
      message: "",
      messageType: "",
      messageValidation: null,
      commentValidation: null,
      fileIcons,
      allowed_file,
      rules: [
        (v) =>
          !v ||
          !v.some((file) => file.size > 2000000) ||
          "File size should be less than 2 MB!",
      ],
    };
  },
  computed: {
    ...mapState({
      meetingChatComments: (state) => state.agenda.meetingChatComments,
    }),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    errorCount() {
      let count = 0;
      for (const item of this.uploadingFiles) {
        if (this.checkStatus(item).status === false) {
          count++;
        }
      }
      return count;
    },
    isDirtyData() {
      return JSON.stringify(this.originalFormData) !== JSON.stringify(this.formData);
    },
  },
  mounted() {
    const thisInstance = this;
    this.formData.tenantid = this.selectedTenant;
    this.originalFormData = _.cloneDeep(this.formData);
    setTimeout(() => {
      thisInstance.showTextEditor = true;
    }, 200);
  },
  watch: {
    formData: {
      handler(newValue, oldValue) {
        const comment = newValue.comment;
        if (
          String(comment)
            .replace(/<(.|\n)*?>/g, "")
            .replace(/\&nbsp;/g, "")
            .trim() !== ""
        ) {
          this.messageValidation = null;
        } else {
          this.messageValidation = this.$t("reqMsgRule");
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      createChatDocuments: "document/createChatDocuments",
      storeMeetingMessage: "agenda/addMeetingMessage",
      updateMeetingMessage: "agenda/updateMeetingMessage",
      listMeetingChatComments: "agenda/listMeetingChatComments",
    }),
    close() {
      this.$emit("input", false);
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    async saveComment() {
      this.commentValidation = this.messageValidation;

      try {
        let formData = new FormData();
        if(this.commentData && this.commentData.id){
          formData.append("id", this.commentData.id);
          this.formData.is_modified = true;
          this.formData.updated_at = new Date().toISOString();
        }
        this.formData.tenantId = this.selectedTenant
        if(this.deleteDocumentIds.length){
          this.formData.deleted_documents = this.deleteDocumentIds
        }
        let index = 0;
        for (const item of this.formData.documents) {
          if(item instanceof File && !item.documentId){
            formData.append(`documents[${index}][tenantId]`, this.selectedTenant);
            formData.append(`documents[${index}][topicId]`, "");
            formData.append(`documents[${index}][chatId]`, this.chatId);
            formData.append(`documents[${index}][meetingId]`, this.meetingObject.id);
            formData.append(`documents[${index}][file]`, item);
            formData.append(`documents[${index}][chunk_upload]`, false);
            formData.append(`documents[${index}][filename]`, item.name);
            formData.append(`documents[${index}][content_type]`, item.type);
            formData.append(`documents[${index}][size]`, item.size);
            formData.append(`documents[${index}][lastModified]`, currentDateTime(item.lastModified));
            index++
          }
        }
        formData.append("chat_data", JSON.stringify(this.formData));
        let response = null;
        if(this.commentData && this.commentData.id){
          response = await this.updateMeetingMessage(formData);
          response.requestType = "edit";
        }else{
          response = await this.storeMeetingMessage(formData);
          response.requestType = "add";
        }
        this.isPrevent = false;
        this.$emit("saveComment", response);
      } catch (error) {
        this.logErrorsAndExceptions(
          "approvedList.getReviewDataSets.error",
          error
        );
        console.error(error);
      }
    },
    getFileIcon(filename) {
      let strArr = filename.split(".");
      let arrlength = strArr.length;
      if (arrlength > 0) {
        return fileIcons[strArr[arrlength - 1].toLowerCase()] || "mdi-file";
      }
      return "mdi-file";
    },
    fileError(err) {
      this.showMessageSheet(
        err.error + " File(s) Size Limit Exceeded",
        "error"
      );
    },
    checkStatus(file) {
      const maxSize = MAX_UPLOAD_SIZE ?? 0;
      let filesWithStatus = {
        filename: file.fileName,
      };
      if (!this.allowed_file.includes(file.type)) {
        filesWithStatus.status = false;
        filesWithStatus.message = this.$t("fileErrorMsg", {
          filename: file.fileName,
        });
      } else if (file.size > maxSize) {
        filesWithStatus.status = false;
        filesWithStatus.message = this.$t("fileSizeErrorMsg", {
          maxsize: formatBytes(maxSize),
        });
      } else {
        filesWithStatus.status = true;
        filesWithStatus.message = "Success";
      }
      return filesWithStatus;
    },
    addUploadingFiles,
    removeUploadingFile,
    descriptionChanges,
  },
};

function descriptionChanges(val) {
  this.isDescriptionWithImage = val;
}

function addUploadingFiles(files) {
  files = Array.from(files);
  this.formData.documents.push(...files);
}

function removeUploadingFile(index) {
  const deletedFile = this.formData.documents[index];
  if(deletedFile.documentId && deletedFile.documentId !== ""){
    this.deleteDocumentIds.push(deletedFile.documentId);
  }
  this.formData.documents.splice(index, 1);
}
</script>

<style scoped>
.error-element {
  color: var(--v-error-base) !important;
}
::v-deep .v-toolbar__content {
  height: 45px !important;
}
</style>